import React from 'react';
import loadable from '@loadable/component';

import { H1 } from '../PageBuilder/Primitives/Heading';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);
const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

const hasContent = data => typeof data?.content === 'string';
const exposeContentAsChildren = data => (hasContent(data) ? { children: data.content } : {});

// Inside a modal the modal's own header is the h2, so the document's title is
// demoted from h1 to keep a single h1 per page (an SEO and screen-reader issue).
const CustomHeading1 = props => <H1 as="h2" {...props} />;

/**
 * The body of a legal document, without the page chrome.
 *
 * Used where a document has to appear inside something else — e.g. the terms
 * and privacy modals on the sign-up form.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.doc a legal document from src/content/legal
 * @returns {JSX.Element}
 */
export const LegalContent = props => {
  const { doc } = props;

  return (
    <SectionBuilder
      {...doc}
      options={{
        fieldComponents: {
          heading1: { component: CustomHeading1, pickValidProps: exposeContentAsChildren },
        },
        isInsideContainer: true,
      }}
    />
  );
};

/**
 * A full page for one of ethy's legal documents.
 *
 * The documents are held in the repo (src/content/legal) rather than in
 * Console's page assets, so they render straight away and need no fetching:
 * there is no loading or error state to handle.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.doc a legal document from src/content/legal
 * @returns {JSX.Element}
 */
const LegalPage = props => {
  const { doc, currentPage } = props;

  return <PageBuilder pageAssetsData={doc} schemaType="WebPage" currentPage={currentPage} />;
};

export default LegalPage;
